import { UserInfoInterface } from 'Interfaces/User/userInterfaces';
import { OverrideItem } from 'stores/JobPasteStore';

export enum MODAL_TYPE {
  // Experiment
  PPS_JPB_PASTE_BULK_UPLOAD_JOBS_MODAL = 'bulkuploadmodal',
  PPS_JPB_PASTE_ADD_JOB_MODAL = 'addJob',
  PPS_JOB_PASTE_OVERRIDE_CAPTURE_MODAL = 'overrideCapture',
  PPS_JOB_PASTE_SCHEDULE_ATTAINMENT_MODAL = 'scheduleAttainment',
  PPS_JOB_PASTE_IMPORT_SYSPRO_JOBS_MODAL = 'importSysproJobs'
}

export type JOB_TYPES = 'released' | 'ready' | 'inProcess' | 'staged' | 'prestaged';

export type PRIORITY_TYPE = '1' | '3';

export type RUN_TYPE = 'run' | 'overlay' | 'recap' | 'dump';

export type CLEAN_TYPE = 'l1' | 'l2' | 'l3';

export const roomList = [
  { label: 'Purple', value: 'PU', color: '#663399' },
  { label: 'Green', value: 'GR', color: '#3CB371' },
  { label: 'Gold', value: 'GO', color: '#FFD700' },
  { label: 'Blue', value: 'BL', color: '#0000FF' },
  { label: 'Black', value: 'BK', color: '#000000' },
  { label: 'Red B', value: 'RB', color: '#8B0000' },
  { label: 'Red C', value: 'RC', color: '#FF0000' },
  { label: 'Red D', value: 'RD', color: '#DC143C' },
  { label: 'White A', value: 'OL', color: '#F5F5F5' },
  { label: 'White B', value: 'WB', color: '#FFFFFF' },
  { label: 'Silver', value: 'SI', color: '#C0C0C0' },
  { label: 'Saps', value: 'SAPS', color: '#9932CC' }
];

// Room ordering priority map based on the specified order
export const ROOM_ORDER_PRIORITY = {
  Purple: 1,
  Green: 2,
  Gold: 3,
  Blue: 4,
  Black: 5,
  'Red B': 6,
  'Red C': 7,
  'Red D': 8,
  'White A': 9,
  'White B': 10,
  Silver: 11,
  Saps: 12
};

// Create reverse mapping from short codes to priority for faster lookup
export const ROOM_SHORT_CODE_TO_PRIORITY: Record<string, number> = {
  PU: 1, // Purple
  GR: 2, // Green
  GO: 3, // Gold
  BL: 4, // Blue
  BK: 5, // Black
  RB: 6, // Red B
  RC: 7, // Red C
  RD: 8, // Red D
  OL: 9, // White A
  WB: 10, // White B
  SI: 11, // Silver
  SA: 12 // Saps
};

// Utility function to get room priority with multiple fallback strategies
const getRoomPriority = (room: { name?: string; shortCode?: string }): number => {
  const roomName = room.name || '';
  const shortCode = room.shortCode || '';

  // Strategy 1: Exact match by name
  let priority = ROOM_ORDER_PRIORITY[roomName as keyof typeof ROOM_ORDER_PRIORITY];
  if (priority) {
    return priority;
  }

  // Strategy 2: Match by short code
  priority = ROOM_SHORT_CODE_TO_PRIORITY[shortCode];
  if (priority) {
    return priority;
  }

  // Strategy 3: Partial name matching (case insensitive)
  const matchedRoomName = Object.keys(ROOM_ORDER_PRIORITY).find(
    key =>
      roomName.toLowerCase().includes(key.toLowerCase()) ||
      key.toLowerCase().includes(roomName.toLowerCase())
  );
  if (matchedRoomName) {
    return ROOM_ORDER_PRIORITY[matchedRoomName as keyof typeof ROOM_ORDER_PRIORITY];
  }

  // Strategy 4: Try to find room by short code in roomList
  const roomListItem = roomList.find(item => item.value === shortCode);
  if (roomListItem) {
    return ROOM_ORDER_PRIORITY[roomListItem.label as keyof typeof ROOM_ORDER_PRIORITY] || 999;
  }

  // Default: unknown rooms go to the end
  return 999;
};

// Utility function to sort rooms based on the specified priority order
export const sortRoomsByPriority = <T extends { name?: string; shortCode?: string }>(
  rooms: T[]
): T[] => {
  return rooms.slice().sort((a, b) => {
    const priorityA = getRoomPriority(a);
    const priorityB = getRoomPriority(b);

    // If both have same priority (both unknown), sort alphabetically by name
    if (priorityA === 999 && priorityB === 999) {
      const nameA = a.name || '';
      const nameB = b.name || '';
      return nameA.localeCompare(nameB);
    }

    return priorityA - priorityB;
  });
};

export const getOverrideNoteTemplate = (overrideItem: OverrideItem, user: UserInfoInterface) => {
  return (
    `"${overrideItem.action}" completed for job ID ${overrideItem.jobId}. ` +
    `Updated from ${overrideItem.valueBefore} to ${overrideItem.valueNow} by ${user.userInfo.name}. ` +
    `Reason: ${overrideItem.reason}.`
  );
};
